import { Injectable, EventEmitter } from '@angular/core';
import 'rxjs';

import {
  Workflow,
  Role,
  User,
  Client,
  ApplicationConfiguration,
  WorkflowApplication
} from '../models';
import { Activity, ActivityModel } from '../models/activities';
import { DataEntity } from '../models/data-entities';
import { Utilities, ActivityUtilities } from './';
import { ActivityFactory, WorkflowService } from './workflow.service';
import { JsonNetDecycle } from './utilities/json-net-decycle';

@Injectable()
export class WorkflowContextService {
  client$: EventEmitter<Client> = new EventEmitter<Client>();
  staffDepartmentId$: EventEmitter<string> = new EventEmitter<string>();
  user$: EventEmitter<Partial<User>> = new EventEmitter<Partial<User>>();
  isLoggedIn$: EventEmitter<boolean> = new EventEmitter<boolean>();
  appConfiguration$: EventEmitter<ApplicationConfiguration> = new EventEmitter<
    ApplicationConfiguration
  >();
  workflow$: EventEmitter<Workflow> = new EventEmitter<Workflow>();
  activeActivity$: EventEmitter<Activity<ActivityModel>> = new EventEmitter<
    Activity<ActivityModel>
  >();
  previewActivity$: EventEmitter<Activity<ActivityModel>> = new EventEmitter<
    Activity<ActivityModel>
  >();
  viewAsRole$: EventEmitter<Role> = new EventEmitter<Role>();
  authToken$: EventEmitter<string> = new EventEmitter<string>();
  authTokenExpiration$: EventEmitter<number> = new EventEmitter<number>();
  refreshToken$: EventEmitter<string> = new EventEmitter<string>();
  applicationId: string;
  applicationId$: EventEmitter<string> = new EventEmitter<string>();
  applicationReturnURL$: EventEmitter<string> = new EventEmitter<string>();
  buildApplicationReturnURL$: EventEmitter<string> = new EventEmitter<string>();
  returnUrl$: EventEmitter<string> = new EventEmitter<string>();
  needsReload$: EventEmitter<boolean> = new EventEmitter<boolean>();
  previousSystemDataWorkflowId$: EventEmitter<string> = new EventEmitter<
    string
  >();
  startTestAppActivityId$: EventEmitter<string> = new EventEmitter<string>();
  isTestApplication$: EventEmitter<boolean> = new EventEmitter<
    boolean>;
  testAsData$: EventEmitter<{ clientId?: string, contractorTypeIds?: string[] }> = new EventEmitter<{ clientId?: string, contractorTypeIds?: string[] }>();
  useNewLayout$: EventEmitter<boolean> = new EventEmitter<boolean>();
  isJanusChildWorkflowCalled$: EventEmitter<boolean> = new EventEmitter<boolean>();
  parentApplicationId$: EventEmitter<string | null> = new EventEmitter<string | null>();

  isLocalEnvironment: boolean;


  // added by ketan 29-12-2025
  // ------------------------
  janusLayout$: EventEmitter<'staff' | 'applicant' | 'none'> = new EventEmitter<'staff' | 'applicant' | 'none'>();
  // -----------------------
  // ended by ketan 29-12-2025
  applicationViewType$: EventEmitter<'overlay' | 'inline' | 'none'> = new EventEmitter<'overlay' | 'inline' | 'none'>();
  _suspendPersist = false;

  authToken: string;
  authTokenExpiration: number;
  refreshToken: string;
  client: Client;
  staffDepartmentId?: string;
  // Added by Amisha on 2026-09-03.
  // Reason: let Client Admin Janus share its already-resolved department name without another API request in routed children.
  staffDepartmentName?: string;
  user: Partial<User>;
  isLoggedIn: boolean;
  appConfiguration: ApplicationConfiguration;
  workflow: Workflow;
  activeActivity: Activity<ActivityModel>;
  previewActivity: Activity<ActivityModel>;
  viewAsRole: Role;
  applicationReturnURL: string;
  buildApplicationReturnURL: string;
  returnUrl: string;
  needsReloaded: boolean;
  versionHash: string;
  hasPreviousSystemData: boolean;
  previousSystemDataWorkflowId: string;
  isProduction: boolean;
  invalidWorkflowCount: number;
  contactSupportLink: string;
  startTestAppActivityId: string;
  isTestApplication: boolean;
  testAsData: { applicationId?: string; clientId?: string, contractorTypeIds?: string[] };
  useNewLayout: boolean;
  //added by priya 2025-11-12
  static isStepperOpen: boolean;
  static isApplicantOpen: boolean=false;
  //end by priya 2025-11-12

  // added by ketan 29-12-2025
  // ------------------------
  janusLayout: 'staff' | 'applicant' | 'none' = 'none'
  // -----------------------
  // ended by ketan 29-12-2025
  applicationViewType :'overlay' | 'inline' | 'none' ='none';
  isJanusChildWorkflowCalled : boolean = false
  parentApplicationId : string|null = null ;

  isLinkedApplicationLaunch: boolean = false;
  linkedApplicationOriginId: string | null = null;
  linkedApplicationOriginRoute: string | null = null; 
  linkedApplicationOriginDeptId: string | null = null;
  persistContext() {
    if (!this._suspendPersist) {
      try {
        const decycled = JsonNetDecycle.decycle({
          client: this.client,
          staffDepartmentId: this.staffDepartmentId,
          user: this.user,
          isLoggedIn: this.isLoggedIn,
          appConfiguration: this.appConfiguration,
          authToken: this.authToken,
          authTokenExpiration: this.authTokenExpiration,
          refreshToken: this.refreshToken,
          viewAsRole: this.viewAsRole,
          testApplicationReturnURL: this.applicationReturnURL,
          buildApplicationReturnURL: this.buildApplicationReturnURL,
          returnUrl: this.returnUrl,
          previousSystemDataWorkflowId: this.previousSystemDataWorkflowId,
          startTestAppActivityId: this.startTestAppActivityId,
          testAsData: this.testAsData,
          useNewLayout: this.useNewLayout,

          // added by ketan 29-12-2025
          // ------------------------
          janusLayout: this.janusLayout,
          // -----------------------
          // ended by ketan 29-12-2025
          applicationViewType:this.applicationViewType,
          isJanusChildWorkflowCalled:this.isJanusChildWorkflowCalled,
          parentApplicationId:this.parentApplicationId


        });
        localStorage.setItem('context', JSON.stringify(decycled));
      } catch (ex) {
        // eat any errors that occurs for troubleshooting
        console.log('error when persisting context', ex);
      }
    }
  }

  checkVersion(serverVersionHash: string) {
    if (
      this.versionHash &&
      serverVersionHash &&
      this.versionHash != serverVersionHash
    ) {
      this.needsReload$.emit(true);
    }
  }

  mapWorkflowActivities(workflow: Workflow): Workflow {
    return ActivityUtilities.convertWorkflowJsonToClasses(workflow);
  }

  searchWorkflowActivities(
    workflow: Workflow,
    searchActivity: Activity<ActivityModel>
  ): Activity<ActivityModel> {
    const flatActivities = ActivityUtilities.flattenActivities(
      workflow.version.graph.getActivities(workflow.designStatus)
    );

    let activity = null;

    activity = flatActivities.find(a => a.id === searchActivity.id);

    if (!(activity instanceof Activity)) {
      activity = ActivityFactory.createActivity(activity, activity);
    }

    return activity;
  }

  restoreContext() {
    try {
      this._suspendPersist = true;
      const context = JsonNetDecycle.retrocycle(
        JSON.parse(localStorage.getItem('context'))
      );

      this.client$.next(context.client);
      this.staffDepartmentId$.next(context.staffDepartmentId);
      this.user$.next(context.user);
      this.isLoggedIn$.next(context.isLoggedIn);
      this.appConfiguration$.next(context.appConfiguration);
      this.authToken$.next(context.authToken);
      this.authTokenExpiration$.next(context.authTokenExpiration);
      this.refreshToken$.next(context.refreshToken);
      this.applicationReturnURL$.next(context.testApplicationReturnURL);
      this.buildApplicationReturnURL$.next(context.buildApplicationReturnURL);
      this.returnUrl$.next(context.returnUrl);
      this.previousSystemDataWorkflowId$.next(
        context.previousSystemDataWorkflowId
      );

      this.viewAsRole$.next(context.viewAsRole);
      this.startTestAppActivityId$.next(context.startTestAppActivityId);

      this.testAsData$.next(context.testAsData);
      this.useNewLayout$.next(context.useNewLayout);

      // added by ketan 29-12-2025
      // ------------------------
      this.janusLayout$.next(context.janusLayout)
      // -----------------------
      // ended by ketan 29-12-2025
      this.applicationViewType$.next(context.applicationViewType)
      this.isJanusChildWorkflowCalled$.next(context.isJanusChildWorkflowCalled)
      this.parentApplicationId$.next(context.parentApplicationId)
      this._suspendPersist = false;
      this.persistContext();
    } catch (ex) {
      this.isLoggedIn$.next(false);
      this.useNewLayout$.next(false);
      this._suspendPersist = false;
    }
  }

  constructor() {
    this.client$.subscribe(client => {
      if (client && client.adminRole) {
        client.adminRole.members = null;
      }
      if (client?.useNewLayout !== undefined) {
      this.useNewLayout$.next(client.useNewLayout);
  }
      this.client = client;
      this.persistContext();
    });

    this.staffDepartmentId$.subscribe(staffDepartmentId => {
      this.staffDepartmentId = staffDepartmentId;
      this.persistContext();
    });

    this.user$.subscribe(user => {
      this.user = user;
      this.persistContext();
    });

    this.isLoggedIn$.subscribe(value => {
      this.isLoggedIn = value;
      this.persistContext();
    });

    this.appConfiguration$.subscribe(appConfig => {
      this.appConfiguration = appConfig;
      this.persistContext();
    });

    this.workflow$.subscribe(workflow => {
      if (workflow && workflow.adminRole) {
        workflow.adminRole.members = null;
      }
      this.workflow = workflow;
      this.persistContext();
    });

    this.activeActivity$.subscribe(activity => {
      if (activity && activity.model.responsibleRole) {
        activity.model.responsibleRole.members = null;
      }
      this.activeActivity = activity;
      this.persistContext();
    });

    this.previewActivity$.subscribe(previewActivity => {
      this.previewActivity = previewActivity;
      this.persistContext();
    });

    this.viewAsRole$.subscribe(viewAsRole => {
      if (viewAsRole) {
        viewAsRole.members = null;
      }
      this.viewAsRole = viewAsRole;
      this.persistContext();
    });

    this.authToken$.subscribe(authToken => {
      this.authToken = authToken;
      this.persistContext();
    });

    this.authTokenExpiration$.subscribe(authTokenExpiration => {
      this.authTokenExpiration = authTokenExpiration;
      this.persistContext();
    });

    this.refreshToken$.subscribe(refreshToken => {
      this.refreshToken = refreshToken;
      this.persistContext();
    });

    this.applicationId$.subscribe(appId => {
      this.applicationId = appId;
      this.persistContext();
    });

    this.applicationReturnURL$.subscribe(value => {
      this.applicationReturnURL = value;
      this.persistContext();
    });

    this.buildApplicationReturnURL$.subscribe(value => {
      this.buildApplicationReturnURL = value;
      this.persistContext();
    });

    this.returnUrl$.subscribe(value => {
      this.returnUrl = value;
      this.persistContext();
    });

    this.needsReload$.subscribe(value => {
      this.needsReloaded = value;
    });

    this.testAsData$.subscribe(value => {
      this.testAsData = value;
      this.persistContext();
    });

    this.previousSystemDataWorkflowId$.subscribe(value => {
      this.previousSystemDataWorkflowId = value;
    });

    this.startTestAppActivityId$.subscribe(value => {
      this.startTestAppActivityId = value;
      this.persistContext();
    });

    this.isTestApplication$.subscribe(value => {
      this.isTestApplication = value;
    });

    this.useNewLayout$.subscribe(value => {
      this.useNewLayout = value;
      this.persistContext();
    });

    // Determine if the environment is local (development) or production based on the hostname
    this.isLocalEnvironment = window.location.hostname === 'localhost';


    // added by ketan 29-12-2025
    // ------------------------
    this.janusLayout$.subscribe(value => {
      this.janusLayout = value
      this.persistContext();
    })
    // -----------------------
    // ended by ketan 29-12-2025
   this.applicationViewType$.subscribe(value => {
      this.applicationViewType = value
      this.persistContext();
    })
    this.isJanusChildWorkflowCalled$.subscribe(value => {
      this.isJanusChildWorkflowCalled = value
      this.persistContext();
    })
    this.parentApplicationId$.subscribe(value => {
      this.parentApplicationId = value
      this.persistContext();
    })

    this.restoreContext();
  }
}
